import React from "react";
import PropTypes from "prop-types";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "@helpers/translate";
import { CustomScrollTable } from "../../CustomScrollTable";
import { useOrdersLayout } from "../OrdersLayoutContext";
import { FavoriteStarIcon } from "./FavoriteStarIcon";
import { CommunityMobileList } from "./CommunityMobileList";
import { getClickableProps } from "./rowProps";
import { COIN_ICONS } from "./communityMockData";
import TradeArrowUpIcon from "@assets/icons/TradeArrowUpIcon";
import TradeArrowDownIcon from "@assets/icons/TradeArrowDownIcon";
import "./CommunityTables.sass";

export const PublicFeedTable = ({ data = [], loading = false, onRowClick }) => {
  const { __ } = useTranslation();
  const isMobileQuery = useMediaQuery("(max-width: 768px)");
  const { isCompactCards } = useOrdersLayout();
  const isMobile = isMobileQuery || isCompactCards;

  if (isMobile) {
    return (
      <CommunityMobileList
        data={data}
        loading={loading}
        type="public-feed"
        onItemClick={onRowClick}
      />
    );
  }

  return (
    <CustomScrollTable className="community-table community-table--public-feed">
      <Table stickyHeader>
        <TableHead>
          <TableRow className="community-table__head-row">
            <TableCell className="community-table__col-username">
              {__("futures.community.username")}
            </TableCell>
            <TableCell className="community-table__col-trade">
              {__("futures.community.trade")}
            </TableCell>
            <TableCell className="community-table__col-wager">
              {__("futures.community.wager")}
            </TableCell>
            <TableCell className="community-table__col-leverage">
              {__("futures.community.leverage")}
            </TableCell>
            <TableCell className="community-table__col-entry">
              {__("futures.community.entry")}
            </TableCell>
            <TableCell className="community-table__col-exit">
              {__("futures.community.exit")}
            </TableCell>
            <TableCell className="community-table__col-profit-loss">
              {__("futures.community.profit_loss")}
            </TableCell>
            <TableCell className="community-table__col-multiplier">
              {__("futures.community.multiplier")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => {
            const isProfit = String(row.profitLoss).startsWith("+");
            const isLoss = String(row.profitLoss).startsWith("-");
            const profitLossClass = isProfit
              ? "community-table__cell--green"
              : isLoss
                ? "community-table__cell--red"
                : "";

            const isMultiplierGreen = isProfit;
            const multiplierClass = isMultiplierGreen
              ? "community-table__cell--green"
              : isLoss
                ? "community-table__cell--red"
                : "";

            const isUp = row.trade?.direction === "up";
            const coinIcon =
              COIN_ICONS[row.trade?.coinIcon] ||
              COIN_ICONS[row.trade?.coin] ||
              null;

            return (
              <TableRow
                key={row.id}
                {...getClickableProps(
                  onRowClick,
                  row,
                  "community-table__body-row",
                  "community-table__body-row--clickable",
                )}
              >
                {/* 1. Username */}
                <TableCell className="community-table__col-username">
                  <div className="community-table__user-cell">
                    <button
                      type="button"
                      className="community-table__star-btn"
                      aria-label="favorite"
                    >
                      <FavoriteStarIcon
                        active={row.favoriteIcon === "star-yellow"}
                        size={24}
                      />
                    </button>
                    <span className="community-table__username">
                      {row.username}
                    </span>
                  </div>
                </TableCell>

                {/* 2. Trade */}
                <TableCell className="community-table__col-trade">
                  <div className="community-table__trade-cell">
                    <span className="community-table__trade-arrow">
                      {isUp ? (
                        <TradeArrowUpIcon size={16} color="#2FA766" />
                      ) : (
                        <TradeArrowDownIcon size={16} color="#E2464A" />
                      )}
                    </span>
                    {coinIcon && (
                      <img
                        src={coinIcon}
                        alt={row.trade?.coin || "coin"}
                        className="community-table__coin-icon"
                      />
                    )}
                  </div>
                </TableCell>

                {/* 3. Wager */}
                <TableCell className="community-table__col-wager">
                  {row.wager}
                </TableCell>

                {/* 4. Leverage */}
                <TableCell className="community-table__col-leverage">
                  {row.leverage}
                </TableCell>

                {/* 5. Entry */}
                <TableCell className="community-table__col-entry">
                  {row.entry}
                </TableCell>

                {/* 6. Exit */}
                <TableCell className="community-table__col-exit">
                  {row.exit}
                </TableCell>

                {/* 7. Profit / Loss */}
                <TableCell
                  className={`community-table__col-profit-loss ${profitLossClass}`}
                >
                  {row.profitLoss}
                </TableCell>

                {/* 8. Multiplier */}
                <TableCell
                  className={`community-table__col-multiplier ${multiplierClass}`}
                >
                  {row.multiplier}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </CustomScrollTable>
  );
};

PublicFeedTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
};

export default PublicFeedTable;
